"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../../../utils/api/auth.js";
import axiosInstance from "../../../../utils/api/axiosInstance.js";
import JsBarcode from "jsbarcode";
import QRCodeLib from "qrcode";
import { generateTicketPDF } from "../../ticketPdf.js";
import GlobalHeader from "../../../../components/GlobalHeader.jsx";
import "../css/ViewTickets.css";
import TicketCard from "../components/TicketCard.jsx";

const ViewTickets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingTicket, setGeneratingTicket] = useState(false);
  const [barcodeUrl, setBarcodeUrl] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  useEffect(() => {
    let isMounted = true;

    const resolveTicket = async () => {
      const { ticket: ticketData } = location.state || {};

      console.log('[ViewTickets] 🎫 Loading ticket from route state:', {
        hasTicket: !!ticketData,
        purchase_id: ticketData?.purchaseId || ticketData?.purchase_id || ticketData?.originalPurchase?.purchase_id,
        ticket_number: ticketData?.ticket_number || ticketData?.ticketNumber,
      });
      if (!ticketData) {
        navigate("/ticketslist");
        return;
      }

      setLoading(true);

      try {
        const purchaseId =
          ticketData.purchaseId ||
          ticketData.purchase_id ||
          ticketData.originalPurchase?.purchase_id ||
          ticketData.originalPurchase?.id;

        let ticketNumber = ticketData.ticket_number || ticketData.ticketNumber;

        if (!ticketNumber && purchaseId) {
          console.log('[ViewTickets] 🔢 Fetching ticket number — API: GET /customer/ticket-number/:purchaseId', { purchaseId });
          const response = await axiosInstance.get(`/customer/ticket-number/${purchaseId}`);
          ticketNumber = response.data?.ticket_number;
          console.log('[ViewTickets] ✅ Ticket number received:', { purchaseId, ticketNumber });
        }

        if (!ticketNumber) {
          alert("Ticket number could not be found. Please return to your tickets list and try again.");
          navigate("/ticketslist");
          return;
        }

        const cleanTicket = {
          ...ticketData,
          purchaseId,
          ticket_number: ticketNumber,
          ticketNumber,
          barcode: ticketNumber,
          qrCode: ticketNumber,
        };
        console.log('[ViewTickets] ✅ Ticket ready to display:', {
          columns: 'ticket_number/ticketNumber, ticketType, eventName, eventDate, eventLocation, delegate[{name,surname,email,phone}], price, status',
          ticket_number: ticketNumber,
          eventName: cleanTicket.eventName,
        });

        if (isMounted) {
          setTicket(cleanTicket);
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to load ticket details", error);
        alert("Unable to load this ticket. Please try again.");
        navigate("/ticketslist");
      }
    };

    resolveTicket();

    return () => {
      isMounted = false;
    };
  }, [location.state, navigate]);

  useEffect(() => {
    if (!ticket) return;

    const code = ticket.ticket_number;

    if (!code) return;

    const generateBarcode = async () => {
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, String(code), {
          format: "CODE128",
          width: 2,
          height: 100,
          displayValue: false,
          margin: 10,
          background: "#FFFFFF",
          lineColor: "#000000",
        });
        setBarcodeUrl(canvas.toDataURL("image/png"));
      } catch (err) {
        console.error("JsBarcode generation failed:", err);
        setBarcodeUrl("");
      }
    };

    const generateQRCode = async () => {
      try {
        const url = await QRCodeLib.toDataURL(String(code), {
          width: 200,
          margin: 2,
          color: { dark: "#000000", light: "#FFFFFF" },
        });
        setQrCodeUrl(url);
      } catch (err) {
        console.error("QR generation failed:", err);
        setQrCodeUrl("");
      }
    };

    Promise.all([generateBarcode(), generateQRCode()]);
  }, [ticket]);

  const formatDate = (dateString) => {
    if (!dateString) return "Date not available";
    try {
      return new Date(dateString).toLocaleDateString("en-ZA", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Date not available";
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === "N/A") return "R 0.00";

    if (typeof price === "string") {
      const cleanPrice = price.replace(/[R\s,]/g, "").replace(/[^\d.]/g, "");
      const numPrice = Number.parseFloat(cleanPrice);
      return isNaN(numPrice) ? "R 0.00" : `R ${numPrice.toFixed(2)}`;
    }

    const numPrice = Number.parseFloat(price);
    return isNaN(numPrice) ? "R 0.00" : `R ${numPrice.toFixed(2)}`;
  };

  const handleBackToList = () => {
    navigate("/customerdash");
  };

  const handleLogout = () => {
    logout(navigate);
  };

  const handleDownloadTicket = async () => {
    if (!ticket) return;

    setGeneratingTicket(true);

    if (!ticket.ticket_number) {
      alert("Ticket number missing. Please reload your ticket from the list.");
      return;
    }

    try {
      const code = ticket.ticket_number;
      
      const qrCodeUrl = await QRCodeLib.toDataURL(code, {
        width: 1024,
        margin: 2,
        errorCorrectionLevel: "M",
      });

      const canvas = document.createElement("canvas");
      JsBarcode(canvas, code, {
        format: "CODE128",
        displayValue: false,
        margin: 10,
        height: 120,
        width: 3,
      });
      const barcodeUrl = canvas.toDataURL("image/png");

      await generateTicketPDF({
        ticket: {
          ...ticket,
          ticket_number: code,
          barcode: code,
          qrCode: code,
        },
        qrCodeUrl,
        barcodeUrl,
      });
    } catch (error) {
      console.error("Error generating ticket PDF:", error);
      alert("Failed to generate ticket PDF. Please try again.");
    } finally {
      setGeneratingTicket(false);
    }
  };

  if (loading) {
    return (
      <div className="ticket-view-page">
        <GlobalHeader
          title="View Ticket"
          subtitle="Your event ticket details"
          backTo="/customerdash"
          logoutTo="/"
          onLogout={handleLogout}
        />
        <div className="ticket-view-loading">
          <div className="ticket-view-loading-card">
            <div className="ticket-view-loading-spinner" />
            <p className="ticket-view-loading-text">Loading ticket...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-view-page">
        <GlobalHeader
          title="View Ticket"
          subtitle="Your event ticket details"
          backTo="/customerdash"
          logoutTo="/"
          onLogout={handleLogout}
        />
        <div className="ticket-view-empty">
          <div className="ticket-view-empty-card">
            <h3 className="ticket-view-empty-title">Ticket Not Found</h3>
            <p className="ticket-view-empty-text">
              The requested ticket could not be found.
            </p>
            <button
              type="button"
              className="ticket-view-empty-button"
              onClick={handleBackToList}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-view-page">
      <GlobalHeader
        title="View Ticket"
        subtitle="Your event ticket details"
        backTo="/customerdash"
        logoutTo="/"
        onLogout={handleLogout}
      />

      <main className="ticket-view-main">
        <TicketCard
          ticket={ticket}
          barcodeUrl={barcodeUrl}
          qrCodeUrl={qrCodeUrl}
          formatDate={formatDate}
          formatPrice={formatPrice}
          onDownload={handleDownloadTicket}
          generatingTicket={generatingTicket}
        />
      </main>
    </div>
  );
};

export default ViewTickets;
